import type { FC, ReactNode } from 'react'
import type { CurrencyCode, Invoice } from '@/types'

import Link from 'next/link'
import { HandCoins } from 'lucide-react'
import { Plate } from '@/components/kit/plate'
import { Button } from '@/components/kit/button'
import { PaymentBadge } from '@/components/kit/badge'
import { PAYMENT_METHOD_LABEL } from '@/constants/carwash'
import { formatMoney, formatTime } from '@/lib/carwash/format'

interface InvoiceRowProps {
    invoice: Invoice
    currency: CurrencyCode
    onCollect: (invoiceId: string) => void
}

export const InvoiceRow: FC<InvoiceRowProps> = ({ invoice, currency, onCollect }): ReactNode => {
    const pending = invoice.status === 'pending'

    return (
        <article className='relative rounded-2xl border border-border bg-card p-3 shadow-card transition-[box-shadow,border-color] hover:border-slate-300 hover:shadow-raised'>
            <Link
                href={`/billing/${invoice.id}`}
                className='absolute inset-0 rounded-2xl focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none'
                aria-label={`Open bill ${invoice.number}`}
            />

            <div className='flex items-start justify-between gap-3'>
                <div className='min-w-0'>
                    <p className='font-mono text-[13px] font-semibold text-slate-600'>
                        {invoice.number}
                        <span className='font-sans font-normal text-muted-foreground'>
                            {` · ${formatTime(invoice.issuedAt)}`}
                        </span>
                    </p>

                    <div className='mt-1.5 flex items-center gap-2'>
                        <Plate value={invoice.plate} size='sm' />
                    </div>

                    <p className='mt-1.5 truncate text-[13px] text-muted-foreground'>
                        {[invoice.serviceName, invoice.vehicleModel].filter(Boolean).join(' · ')}
                    </p>
                </div>

                <div className='flex shrink-0 flex-col items-end gap-1.5'>
                    <p className='font-heading text-lg font-extrabold text-foreground tabular-nums'>
                        {formatMoney(invoice.total, currency)}
                    </p>

                    <PaymentBadge status={invoice.status} />

                    {!pending && invoice.paymentMethod && (
                        <p className='text-xs font-medium text-muted-foreground'>
                            {PAYMENT_METHOD_LABEL[invoice.paymentMethod]}
                        </p>
                    )}
                </div>
            </div>

            {pending && (
                <div className='relative z-10 mt-3 flex justify-end'>
                    <Button size='sm' variant='success' onClick={() => onCollect(invoice.id)}>
                        <HandCoins aria-hidden='true' />
                        Collect payment
                    </Button>
                </div>
            )}
        </article>
    )
}
